import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { countryColors, flagEmoji } from '@shared/types';
import {
  POPULAR_COUNTRIES,
  allCountries,
  countryName,
  dismissSuggestion,
  indexLetter,
  isTerritory,
  pushRecentCountry,
  recentCountries,
  searchCountries,
  suggestedCountry,
  suggestionDismissed,
  type CountryEntry,
} from '@/lib/countries';

interface CountryPickerProps {
  /** País elegido (código) o null = color personalizado. */
  value: string | null | undefined;
  onChange: (country: string | null) => void;
  disabled?: boolean;
  compact?: boolean;
}

type Row = { kind: 'header'; id: string; label: string } | { kind: 'item'; key: string; code: string | null; entry?: CountryEntry };

const TOAST_MS = 1800;

/**
 * Selector de país (ver docs/PAISES.md):
 *  - Recientes (últimos 5) y Populares arriba; luego los miembros de la ONU
 *    de la A a la Z y al final «Territorios y otros» (también elegibles).
 *  - Buscador con el cursor ya puesto (abre el teclado en el celular), sin
 *    tildes y con alias (EEUU, RD, Holanda, UK, Gringo…), con ✕ para borrar.
 *  - Celular: hoja a pantalla completa desde abajo, filas altas, bandera
 *    grande e índice A–Z al costado. PC: ventana centrada; ↑/↓, Enter, Escape.
 *  - Elegir aplica al instante, cierra y avisa «Uniforme actualizado».
 *  - La primera vez sugiere el país del idioma o la zona horaria (un toque).
 */
export const CountryPicker = ({ value, onChange, disabled, compact }: CountryPickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [toast, setToast] = useState(false);
  const [suggest, setSuggest] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const colors = countryColors(value);

  // Sugerencia la primera vez (sin país elegido y sin descartarla).
  useEffect(() => {
    if (value || suggestionDismissed()) setSuggest(null);
    else setSuggest(suggestedCountry());
  }, [value]);

  const rows = useMemo<Row[]>(() => {
    if (!open) return [];
    const q = query.trim();
    if (q) {
      return searchCountries(q).map((e) => ({ kind: 'item' as const, key: e.code, code: e.code, entry: e }));
    }
    const byCode = new Map(allCountries().map((e) => [e.code, e]));
    const out: Row[] = [{ kind: 'item', key: 'custom', code: null }];
    const recents = recentCountries();
    if (recents.length) {
      out.push({ kind: 'header', id: 'sec-recientes', label: 'Recientes' });
      for (const c of recents) out.push({ kind: 'item', key: `r-${c}`, code: c, entry: byCode.get(c) });
    }
    out.push({ kind: 'header', id: 'sec-populares', label: 'Populares' });
    for (const c of POPULAR_COUNTRIES) out.push({ kind: 'item', key: `p-${c}`, code: c, entry: byCode.get(c) });
    let letter = '';
    for (const e of allCountries()) {
      if (isTerritory(e.code)) continue;
      const l = indexLetter(e.name);
      if (l !== letter) {
        letter = l;
        out.push({ kind: 'header', id: `sec-${l}`, label: l });
      }
      out.push({ kind: 'item', key: e.code, code: e.code, entry: e });
    }
    out.push({ kind: 'header', id: 'sec-territorios', label: 'Territorios y otros' });
    for (const e of allCountries()) if (isTerritory(e.code)) out.push({ kind: 'item', key: `t-${e.code}`, code: e.code, entry: e });
    return out;
  }, [open, query]);
  const items = rows.filter((r): r is Extract<Row, { kind: 'item' }> => r.kind === 'item');
  const letters = useMemo(() => rows.filter((r) => r.kind === 'header' && r.label.length === 1).map((r) => (r as { label: string }).label), [rows]);

  // Al abrir: el cursor en el buscador dentro del mismo toque (así el celular abre el teclado).
  const openSheet = () => {
    if (disabled) return;
    flushSync(() => {
      setOpen(true);
      setQuery('');
      setActive(0);
    });
    input.current?.focus({ preventScroll: true });
  };
  const close = () => setOpen(false);
  const pick = (code: string | null) => {
    if (code) pushRecentCountry(code);
    dismissSuggestion();
    setSuggest(null);
    onChange(code);
    setOpen(false);
    setQuery('');
    setToast(true);
  };
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(false), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);
  // Bloquea el scroll de la página detrás de la hoja.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  // La fila activa (teclado) siempre a la vista.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-row-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[active];
      if (it) pick(it.code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
    e.stopPropagation(); // no mueve al jugador ni la zona del lanzador
  };
  const jump = (l: string) => listRef.current?.querySelector(`#cp-sec-${l}`)?.scrollIntoView({ block: 'start' });

  let itemIndex = -1;
  const sheet = open
    ? createPortal(
        <div data-keep-menu className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 sm:items-center" onPointerDown={(e) => e.target === e.currentTarget && close()}>
          <div
            role="dialog"
            aria-label="Elegir país"
            data-testid="country-sheet"
            onKeyDown={onKeyDown}
            className="flex h-[100dvh] w-full flex-col bg-ink-800 shadow-2xl sm:h-[75vh] sm:max-w-md sm:rounded-2xl sm:border sm:border-white/10"
          >
            <div className="flex items-center gap-2 border-b border-white/10 p-3">
              <div className="relative flex-1">
                <input
                  ref={input}
                  type="search"
                  inputMode="search"
                  enterKeyHint="go"
                  autoComplete="off"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  placeholder="Buscar país…"
                  aria-label="Buscar país"
                  className="h-11 w-full rounded-xl border border-white/10 bg-ink-700/80 pl-3 pr-10 text-base text-slate-100 outline-none focus:border-accent-500 [&::-webkit-search-cancel-button]:hidden"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Borrar búsqueda"
                    onClick={() => {
                      setQuery('');
                      setActive(0);
                      input.current?.focus();
                    }}
                    className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-slate-300 hover:bg-white/10"
                  >
                    ✕
                  </button>
                )}
              </div>
              <button type="button" onClick={close} className="h-11 rounded-xl px-3 text-sm font-semibold text-slate-200 hover:bg-white/10">
                Cerrar
              </button>
            </div>
            <div className="relative flex min-h-0 flex-1">
              <div ref={listRef} role="listbox" aria-label="Países" className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {rows.map((r) => {
                  if (r.kind === 'header') {
                    return (
                      <div key={r.id} id={`cp-${r.id}`} data-section={r.label} className="sticky top-0 z-10 bg-ink-800/95 px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur">
                        {r.label}
                      </div>
                    );
                  }
                  itemIndex += 1;
                  const i = itemIndex;
                  const selected = (value ?? null) === r.code;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-code={r.code ?? ''}
                      data-row-index={i}
                      onClick={() => pick(r.code)}
                      onPointerEnter={() => setActive(i)}
                      className={`flex min-h-12 w-full items-center gap-3 px-4 text-left text-base ${
                        i === active ? 'bg-white/10' : ''
                      } ${selected ? 'font-semibold text-accent-400' : 'text-slate-100'}`}
                    >
                      <span className="w-9 text-center text-2xl leading-none">{r.code ? flagEmoji(r.code) : '🎨'}</span>
                      <span className="min-w-0 flex-1 truncate">{r.code ? r.entry?.name ?? countryName(r.code) : 'Color personalizado'}</span>
                      {r.code && query && isTerritory(r.code) && <span className="text-[10px] uppercase text-slate-500">territorio</span>}
                      {selected && <span aria-hidden>✓</span>}
                    </button>
                  );
                })}
                {items.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-slate-300">
                    No encontramos «{query.trim()}». Prueba con otro nombre o sin abreviar (p. ej. «Estados Unidos»).
                  </div>
                )}
              </div>
              {!query && letters.length > 0 && (
                <nav aria-label="Saltar a letra" className="flex w-6 flex-col items-center justify-center gap-0 py-1 text-[10px] font-bold text-accent-400">
                  {letters.map((l) => (
                    <button key={l} type="button" onClick={() => jump(l)} className="leading-[1.35] hover:text-white" aria-label={`Ir a la ${l}`}>
                      {l}
                    </button>
                  ))}
                </nav>
              )}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div data-testid="country-picker" className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Elegir país"
        onClick={openSheet}
        className={`flex w-full items-center gap-2 rounded-lg border border-white/10 bg-ink-700/60 text-left text-slate-100 hover:border-white/30 disabled:opacity-50 ${
          compact ? 'min-h-9 px-2 text-xs' : 'max-w-xs px-2.5 py-1.5 text-sm'
        }`}
      >
        <span className="text-lg leading-none">{value ? flagEmoji(value) : '🎨'}</span>
        <span className="min-w-0 flex-1 truncate">{value ? countryName(value) : 'Color personalizado'}</span>
        {colors && (
          <span className="flex shrink-0 overflow-hidden rounded-full border border-white/20" aria-hidden>
            <span className="h-3.5 w-2.5" style={{ background: colors[0] }} />
            <span className="h-3.5 w-2.5" style={{ background: colors[1] }} />
          </span>
        )}
        <span className="text-slate-400" aria-hidden>
          ▾
        </span>
      </button>
      {suggest && !value && (
        <div data-testid="country-suggestion" className="mt-1 flex items-center gap-1.5 text-xs text-slate-300">
          <button type="button" onClick={() => pick(suggest)} className="rounded-full border border-accent-500/50 bg-accent-500/15 px-2.5 py-1 font-semibold text-white">
            {flagEmoji(suggest)} ¿{countryName(suggest)}? Elegir
          </button>
          <button
            type="button"
            aria-label="Descartar sugerencia"
            onClick={() => {
              dismissSuggestion();
              setSuggest(null);
            }}
            className="rounded-full px-1.5 py-1 text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
      {sheet}
      {toast &&
        createPortal(
          <div role="status" data-testid="country-toast" className="pointer-events-none fixed inset-x-0 bottom-24 z-[110] flex justify-center px-4">
            <div className="rounded-full bg-emerald-600/95 px-4 py-2 text-sm font-semibold text-white shadow-xl">
              {value ? `${flagEmoji(value)} ` : '🎨 '}Uniforme actualizado
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
